import React, { useState, useEffect } from 'react';
import { Modal, Spinner, Button, Badge } from 'react-bootstrap';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import * as XLSX from 'xlsx';
import { FaDownload, FaEye, FaCalendarAlt, FaFilter, FaHospital, FaMapMarkerAlt } from 'react-icons/fa';
import styles from './ProjectVisitsModal.module.css';

// Move formatTimestamp outside of components so both can use it
const formatTimestamp = (timestamp) => {
  if (!timestamp) return "-";
  if (timestamp.seconds) {
    return new Date(timestamp.seconds * 1000).toLocaleString('tr-TR');
  }
  return timestamp;
};

// Small helper so JSX is less verbose
const InfoItem = ({ label, value }) => (
  <div className={styles.infoItem}>
    <span className={styles.infoLabel}>{label}</span>
    <span className={styles.infoValue}>{value ?? '-'}</span>
  </div>
);

const VisitDetailsModal = ({ show, onHide, visit }) => {
  const [craName, setCraName] = useState("");
  const [projectTitle, setProjectTitle] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      if (visit.selectedCra) {
        try {
          const craDoc = await getDoc(doc(db, 'ExternalPersonnels', visit.selectedCra));
          if (craDoc.exists()) {
            const craData = craDoc.data();
            setCraName(`${craData['Adı'] || ''} ${craData['Soyadı'] || ''}`.trim());
          }
        } catch (error) {
          console.error('Error fetching CRA details:', error);
          setCraName("Error loading CRA name");
        }
      }
      if (visit.projectId) {
        try {
          const projectDoc = await getDoc(doc(db, 'Projects', visit.projectId));
          if (projectDoc.exists()) {
            const pd = projectDoc.data();
            setProjectTitle(pd.titleShort || pd.title || pd.name || '');
          }
        } catch (error) {
          console.error('Error fetching project details:', error);
        }
      }
    };
    fetchData();
  }, [visit.selectedCra, visit.projectId]);

  const ev = visit.visitEvaluation || {};

  const val = (v) => v || '-';

  const formatBool = (v) => {
    if (v === true || v === 'yes' || v === 'evet') return 'Evet';
    if (v === false || v === 'no' || v === 'hayır') return 'Hayır';
    return v || '-';
  };

  const computeCycleTime = () => {
    if (ev.cycleTime) return ev.cycleTime;
    const startDate = visit.date;
    const endDate = ev.processEndDate;
    if (startDate && endDate) {
      const diffMs = new Date(endDate) - new Date(startDate);
      if (!isNaN(diffMs) && diffMs >= 0) return Math.round(diffMs / 86400000);
    }
    return '-';
  };

  const computeVisitDuration = () => {
    if (ev.visitDurationHours !== undefined) {
      return { hours: val(String(ev.visitDurationHours)), minutes: val(String(ev.visitDurationMinutes)) };
    }
    const s = visit.startTime, e = visit.endTime;
    if (s && e) {
      const [sh, sm] = s.split(':').map(Number);
      const [eh, em] = e.split(':').map(Number);
      const total = (eh * 60 + em) - (sh * 60 + sm);
      if (total >= 0) return { hours: String(Math.floor(total / 60)), minutes: String(total % 60) };
    }
    return { hours: '-', minutes: '-' };
  };

  const renderHastaneFields = () => (
    <div className={styles.detailSection}>
      <h6 className={styles.sectionTitle}>Hastane Visit Detayları</h6>
      <div className={styles.infoGrid}>
        <InfoItem label="Proje Adı" value={val(projectTitle)} />
        <InfoItem label="Merkez ID" value={val(visit.hospital)} />
        <InfoItem label="Visit ID" value={val(visit.visitNumber || visit.id)} />
        <InfoItem label="Visit Tipi" value={val(visit.visitType)} />
        <InfoItem label="Site Koordinatörü" value={val(visit.siteCoordinator || craName || visit.employeeName)} />
        <InfoItem label="Hasta ID" value={val(visit.patientId)} />
        <InfoItem label="Screen Fail?" value={formatBool(ev.screenFail)} />
        <InfoItem label="Drop Patient?" value={formatBool(ev.dropPatient)} />
        <InfoItem label="Visit Tarihi" value={val(visit.date)} />
        <InfoItem label="Visit Başlangıç Saati" value={val(visit.startTime)} />
        <InfoItem label="Protokol Sapması" value={formatBool(ev.protocolDeviation)} />
        <InfoItem label="CAPA Açıldı mı?" value={formatBool(ev.capaOpened)} />
        <InfoItem label="Yorumlar" value={val(visit.information)} />
        <InfoItem label="eCRF Tamamlanma Tarihi" value={val(ev.ecrfCompletionDate)} />
        <InfoItem label="eCRF Tamamlanma Saati" value={val(ev.ecrfCompletionTime)} />
        <InfoItem label="Kaynak Dok. Tamamlanma Tarihi" value={val(ev.sourceDocCompletionDate)} />
        <InfoItem label="Kaynak Dok. Tamamlanma Saati" value={val(ev.sourceDocCompletionTime)} />
        <InfoItem label="İmza Tamamlanma Tarihi" value={val(ev.signatureCompletionDate)} />
        <InfoItem label="İmza Tamamlanma Saati" value={val(ev.signatureCompletionTime)} />
        <InfoItem label="Süreç Bitiş Tarihi" value={val(ev.processEndDate)} />
        <InfoItem label="Döngü Süresi (gün)" value={computeCycleTime()} />
        <InfoItem label="Sorun Oluştu mu?" value={formatBool(ev.issueOccurred)} />
        <InfoItem label="SLA Hedefi (gün)" value={val(ev.slaTarget)} />
        <InfoItem label="SLA Karşılandı mı?" value={formatBool(ev.slaMet)} />
        <InfoItem label="SLA Gecikti mi?" value={formatBool(ev.slaOverdue)} />
      </div>
    </div>
  );

  const renderHomeFields = () => {
    const duration = computeVisitDuration();
    return (
      <>
        <div className={styles.detailSection}>
          <h6 className={styles.sectionTitle}>Home Visit Detayları</h6>
          <div className={styles.infoGrid}>
            <InfoItem label="Protokol No." value={val(ev.protocolNo || projectTitle)} />
            <InfoItem label="Site ID" value={val(visit.hospital)} />
            <InfoItem label="Hasta ID" value={val(visit.patientId)} />
            <InfoItem label="Visit ID" value={val(visit.visitNumber || visit.id)} />
            <InfoItem label="Pencere Açılış Tarihi" value={val(ev.windowOpenDate)} />
            <InfoItem label="Hedef Tarih" value={val(ev.targetDate)} />
            <InfoItem label="Pencere Kapanış Tarihi" value={val(ev.windowCloseDate)} />
            <InfoItem label="Site Koordinatörü" value={val(visit.siteCoordinator || craName || visit.employeeName)} />
            <InfoItem label="Hasta Durumu" value={val(ev.subjectStatus)} />
            <InfoItem label="Visit Başlangıç Tarihi" value={val(visit.date)} />
            <InfoItem label="Visit Başlangıç Saati" value={val(visit.startTime)} />
            <InfoItem label="Visit Bitiş Tarihi" value={val(ev.visitEndDate || visit.date)} />
            <InfoItem label="Visit Bitiş Saati" value={val(visit.endTime)} />
            <InfoItem label="Visit Süresi - Saat" value={duration.hours} />
            <InfoItem label="Visit Süresi - Dakika" value={duration.minutes} />
            <InfoItem label="eCRF Tamamlanma Tarihi" value={val(ev.ecrfCompletionDate)} />
            <InfoItem label="eCRF Tamamlanma Saati" value={val(ev.ecrfCompletionTime)} />
            <InfoItem label="Kaynak Dok. Tamamlanma Tarihi" value={val(ev.sourceDocCompletionDate)} />
            <InfoItem label="Kaynak Dok. Tamamlanma Saati" value={val(ev.sourceDocCompletionTime)} />
            <InfoItem label="İmza Tamamlanma Tarihi" value={val(ev.signatureCompletionDate)} />
            <InfoItem label="İmza Tamamlanma Saati" value={val(ev.signatureCompletionTime)} />
            <InfoItem label="Süreç Bitiş Tarihi" value={val(ev.processEndDate)} />
            <InfoItem label="Döngü Süresi (gün)" value={computeCycleTime()} />
          </div>
        </div>
        <div className={styles.detailSection}>
          <h6 className={styles.sectionTitle}>Sorun & Düzeltme Takibi</h6>
          <div className={styles.infoGrid}>
            <InfoItem label="Visit Sırasında Sorun Oluştu mu?" value={formatBool(ev.issueOccurred)} />
            <InfoItem label="Sorun Protokol Sapmasına Yol Açtı mı?" value={formatBool(ev.protocolDeviation)} />
            <InfoItem label="Sorunun Kaynağı" value={val(ev.issueDetails || ev.issueCategory)} />
            <InfoItem label="SMO Düzeltme Talepleri" value={val(ev.smoCorrectionRequests)} />
            <InfoItem label="Kalite Yöneticisi Kontrolü" value={val(ev.qualityManagerCheck)} />
            <InfoItem label="CRA Talebi" value={val(ev.craRequest)} />
            <InfoItem label="Düzeltme Nedeni / Yorum" value={val(ev.correctionReason)} />
            <InfoItem label="İlk Kaynak Alınma Tarihi" value={val(ev.dateInitialSourceReceived)} />
            <InfoItem label="CPM'den Son Revizyon Talebi Tarihi" value={val(ev.dateLatestSourceRevision)} />
            <InfoItem label="Kaynak Kesinleşme Tarihi" value={val(ev.dateSourceFinalized)} />
            <InfoItem label="Aksiyon / CAPA Alındı mı?" value={formatBool(ev.capaOpened)} />
            <InfoItem label="DCF Oluşturuldu mu?" value={formatBool(ev.dcfCreated)} />
            <InfoItem label="Kapatıldı mı?" value={formatBool(ev.isClosed)} />
            <InfoItem label="Başlangıç Tarihi" value={val(ev.capaStartDate)} />
            <InfoItem label="Kapanış Tarihi" value={val(ev.capaClosedDate)} />
            <InfoItem label="Hedefe Ulaşıldı mı?" value={formatBool(ev.targetAchieved)} />
            <InfoItem label="Şiddet Derecesi" value={val(ev.severity)} />
          </div>
        </div>
      </>
    );
  };

  const downloadExcel = () => {
    const ev = visit.visitEvaluation || {};
    // Create basic information object
    const basicInfo = {
      'Tarih': visit.date || "-",
      'Visit Tipi': visit.visitType || "-",
      'Başlangıç Saati': visit.startTime || "-",
      'Bitiş Saati': visit.endTime || "-",
      'Hasta ID': visit.patientId || "-",
      'Doktor': visit.doctor || "-",
      'Hastane': visit.hospital || "-",
      'Şehir': visit.city || "-",
      'Seyahat Tipi': visit.travelType || "-",
      'Durum': visit.status ? "Tamamlandı" : "Devam Ediyor",
      'Kaynak Döküman': visit.sourceDocument || "-",
      'Kaynak Döküman Tamamlanma Saati': visit.sourceDocumentCompletionTime || "-",
      'Bilgi': visit.information || "-",
      'Oluşturulma Tarihi': formatTimestamp(visit.createdAt),
      'Son Güncelleme': visit.lastUpdated || "-",
      'Tamamlanma Tarihi': visit.completedAt || "-",
      'İlk Visit Tarihi': visit.firstVisitDate || "-",
      'Çalışan Adı': visit.employeeName || "-",
      'Seçilen CRA': craName || "-",
      'Periyot': visit.period || "-",
      'Opsiyon': visit.option || "-",
      // visitEvaluation fields
      'eCRF Tamamlanma Tarihi': ev.ecrfCompletionDate || "-",
      'eCRF Tamamlanma Saati': ev.ecrfCompletionTime || "-",
      'Kaynak Dok. Tamamlanma Tarihi': ev.sourceDocCompletionDate || "-",
      'Kaynak Dok. Tamamlanma Saati': ev.sourceDocCompletionTime || "-",
      'İmza Tamamlanma Tarihi': ev.signatureCompletionDate || "-",
      'İmza Tamamlanma Saati': ev.signatureCompletionTime || "-",
      'Süreç Bitiş Tarihi': ev.processEndDate || "-",
      'Protokol Sapması': ev.protocolDeviation ? "Evet" : "Hayır",
      'CAPA Açıldı mı?': ev.capaOpened ? "Evet" : "Hayır",
      'Sorun Oluştu mu?': ev.issueOccurred ? "Evet" : "Hayır",
      'SLA Hedefi (gün)': ev.slaTarget || "-",
    };

    // Create combined todo list object including both regular and pre-visit items
    const todoLabels = {
      // Pre-visit items
      pre_patientCalled: 'Hasta arandı mı?',
      pre_nurseTransferArranged: 'Hemşire transferi ayarlandı mı?',
      pre_courierArranged: 'Kurye ayarlandı mı?',
      pre_hospitalApproval: 'Hastane onayı alındı mı?',
      pre_nurseEquipmentReady: 'Hemşire ekipmanı hazır mı?',
      pre_accommodationPlanned: 'Konaklama planlandı mı?',
      
      // Regular todo items
      patientConsent: 'Hasta onamı alındı mı?',
      protocolUpdate: 'Protokol güncellemesi yapıldı mı?',
      kvkkApproval: 'KVKK onayı alındı mı?',
      visitNumberCheck: 'Visit numarası kontrol edildi mi?',
      projectCheck: 'Proje kontrolü yapıldı mı?',
      patientInformed: 'Hasta bilgilendirildi mi?',
      sampleCollection: 'Numune toplama yapıldı mı?',
      sampleShipping: 'Numune kargolama yapıldı mı?',
      sourceDocumentsShared: 'Ziyaret sırasında doldurulan kaynak dökümanlar çalışma ekibi ile e-posta olarak paylaşıldı mı?',
      transferForm: 'Transfer formu dolduruldu mu?',
      documentShipping: 'Kaynak dökümanlar kargolandı mı?',
      
      // Missing keys from console
      sourceDocuments: 'Kaynak dökümanlar hazırlandı mı?',
      sourceDocumentsApproval: 'Kaynak dökümanlar onaylandı mı?',
      workflowCheck: 'İş akışı kontrolü yapıldı mı?',
      medicationCheck: 'İlaç kontrolü yapıldı mı?',
      
      // Additional possible items
      equipmentCheck: 'Ekipman kontrolü yapıldı mı?',
      medicationAdministered: 'İlaç uygulaması yapıldı mı?',
      adverseEventCheck: 'Advers olay kontrolü yapıldı mı?',
      vitalSignsRecorded: 'Vital bulgular kaydedildi mi?',
      labSamplesCollected: 'Laboratuvar örnekleri toplandı mı?',
      concomitantMedsReviewed: 'Eşzamanlı ilaçlar gözden geçirildi mi?',
      inclusionExclusionCheck: 'Dahil/Hariç kriterleri kontrol edildi mi?',
      informedConsentSigned: 'Bilgilendirilmiş onam imzalandı mı?',
      randomizationCompleted: 'Randomizasyon tamamlandı mı?',
      drugAccountability: 'İlaç hesap verebilirliği yapıldı mı?',
      safetyAssessment: 'Güvenlik değerlendirmesi yapıldı mı?',
      queryResolution: 'Query çözümü yapıldı mı?',
      protocolDeviation: 'Protokol sapması var mı?',
      saeReporting: 'SAE raporlaması yapıldı mı?',
      dataEntryCompleted: 'Veri girişi tamamlandı mı?',
      sourceDocumentVerification: 'Kaynak doküman doğrulaması yapıldı mı?',
      monitoringVisitCompleted: 'Monitörleme ziyareti tamamlandı mı?',
      regulatoryDocumentsUpdated: 'Düzenleyici dokümanlar güncellendi mi?',
      siteFileUpdated: 'Site dosyası güncellendi mi?',
      trainingCompleted: 'Eğitim tamamlandı mı?',
      delegationLogUpdated: 'Delegasyon logu güncellendi mi?',
      temperatureLogChecked: 'Sıcaklık logu kontrol edildi mi?',
      pharmacyVisit: 'Eczane ziyareti yapıldı mı?',
      labCertificatesReviewed: 'Laboratuvar sertifikaları gözden geçirildi mi?',
      cvUpdated: 'CV güncellendi mi?',
      licenseVerified: 'Lisans doğrulandı mı?',
      facilityInspection: 'Tesis denetimi yapıldı mı?',
      equipmentCalibration: 'Ekipman kalibrasyonu yapıldı mı?',
      storageConditionsVerified: 'Saklama koşulları doğrulandı mı?',
      shippingDocumentsReviewed: 'Kargo dokümanları gözden geçirildi mi?',
      temperatureExcursionReported: 'Sıcaklık sapması rapor edildi mi?',
      inventoryReconciliation: 'Envanter mutabakatı yapıldı mı?',
      wastageDocumented: 'Fire belgelendi mi?',
      returnShipmentArranged: 'İade kargolama ayarlandı mı?',
      destructionDocumented: 'İmha belgelendi mi?',
      archivingCompleted: 'Arşivleme tamamlandı mı?',
      closeoutVisitScheduled: 'Kapanış ziyareti planlandı mı?',
      finalReportSubmitted: 'Final rapor sunuldu mu?',
      paymentProcessed: 'Ödeme işlendi mi?',
      feedbackCollected: 'Geri bildirim toplandı mı?',
      lessonsLearnedDocumented: 'Öğrenilen dersler belgelendi mi?'
    };

    const todoListData = Object.entries(visit.todoList || {}).reduce((acc, [key, value]) => {
      const label = todoLabels[key] || key;
      const prefix = key.startsWith('pre_') ? 'Ön Ziyaret - ' : 'Yapılacak - ';
      acc[`${prefix}${label}`] = value ? "✓" : "✗";
      return acc;
    }, {});

    // Combine all data
    const dataToExport = [{ ...basicInfo, ...todoListData }];

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Visit Detayları');
    XLSX.writeFile(workbook, `Visit_Detay_${visit.date || 'Tarihsiz'}.xlsx`);
  };

  const isHastane = visit.visitType === 'hastane';
  const isHome = visit.visitType === 'home';

  const todoLabels = {
    pre_patientCalled: 'Hasta arandı mı?',
    pre_nurseTransferArranged: 'Hemşire transferi ayarlandı mı?',
    pre_courierArranged: 'Kurye ayarlandı mı?',
    pre_hospitalApproval: 'Hastane onayı alındı mı?',
    pre_nurseEquipmentReady: 'Hemşire ekipmanı hazır mı?',
    pre_accommodationPlanned: 'Konaklama planlandı mı?',
    patientConsent: 'Hasta onamı alındı mı?',
    protocolUpdate: 'Protokol güncellemesi yapıldı mı?',
    kvkkApproval: 'KVKK onayı alındı mı?',
    visitNumberCheck: 'Visit numarası kontrol edildi mi?',
    projectCheck: 'Proje kontrolü yapıldı mı?',
    patientInformed: 'Hasta bilgilendirildi mi?',
    sampleCollection: 'Numune toplama yapıldı mı?',
    sampleShipping: 'Numune kargolama yapıldı mı?',
    sourceDocumentsShared: 'Ziyaret sırasında doldurulan kaynak dökümanlar çalışma ekibi ile e-posta olarak paylaşıldı mı?',
    transferForm: 'Transfer formu dolduruldu mu?',
    documentShipping: 'Kaynak dökümanlar kargolandı mı?',
    sourceDocuments: 'Kaynak dökümanlar hazırlandı mı?',
    sourceDocumentsApproval: 'Kaynak dökümanlar onaylandı mı?',
    workflowCheck: 'İş akışı kontrolü yapıldı mı?',
    medicationCheck: 'İlaç kontrolü yapıldı mı?',
    equipmentCheck: 'Ekipman kontrolü yapıldı mı?',
    medicationAdministered: 'İlaç uygulaması yapıldı mı?',
    adverseEventCheck: 'Advers olay kontrolü yapıldı mı?',
    vitalSignsRecorded: 'Vital bulgular kaydedildi mi?',
    labSamplesCollected: 'Laboratuvar örnekleri toplandı mı?',
    concomitantMedsReviewed: 'Eşzamanlı ilaçlar gözden geçirildi mi?',
    inclusionExclusionCheck: 'Dahil/Hariç kriterleri kontrol edildi mi?',
    informedConsentSigned: 'Bilgilendirilmiş onam imzalandı mı?',
    randomizationCompleted: 'Randomizasyon tamamlandı mı?',
    drugAccountability: 'İlaç hesap verebilirliği yapıldı mı?',
    safetyAssessment: 'Güvenlik değerlendirmesi yapıldı mı?',
    queryResolution: 'Query çözümü yapıldı mı?',
    protocolDeviation: 'Protokol sapması var mı?',
    saeReporting: 'SAE raporlaması yapıldı mı?',
    dataEntryCompleted: 'Veri girişi tamamlandı mı?',
    sourceDocumentVerification: 'Kaynak doküman doğrulaması yapıldı mı?',
    monitoringVisitCompleted: 'Monitörleme ziyareti tamamlandı mı?',
    regulatoryDocumentsUpdated: 'Düzenleyici dokümanlar güncellendi mi?',
    siteFileUpdated: 'Site dosyası güncellendi mi?',
    trainingCompleted: 'Eğitim tamamlandı mı?',
    delegationLogUpdated: 'Delegasyon logu güncellendi mi?',
    temperatureLogChecked: 'Sıcaklık logu kontrol edildi mi?',
    pharmacyVisit: 'Eczane ziyareti yapıldı mı?',
    labCertificatesReviewed: 'Laboratuvar sertifikaları gözden geçirildi mi?',
    cvUpdated: 'CV güncellendi mi?',
    licenseVerified: 'Lisans doğrulandı mı?',
    facilityInspection: 'Tesis denetimi yapıldı mı?',
    equipmentCalibration: 'Ekipman kalibrasyonu yapıldı mı?',
    storageConditionsVerified: 'Saklama koşulları doğrulandı mı?',
    shippingDocumentsReviewed: 'Kargo dokümanları gözden geçirildi mi?',
    temperatureExcursionReported: 'Sıcaklık sapması rapor edildi mi?',
    inventoryReconciliation: 'Envanter mutabakatı yapıldı mı?',
    wastageDocumented: 'Fire belgelendi mi?',
    returnShipmentArranged: 'İade kargolama ayarlandı mı?',
    destructionDocumented: 'İmha belgelendi mi?',
    archivingCompleted: 'Arşivleme tamamlandı mı?',
    closeoutVisitScheduled: 'Kapanış ziyareti planlandı mı?',
    finalReportSubmitted: 'Final rapor sunuldu mu?',
    paymentProcessed: 'Ödeme işlendi mi?',
    feedbackCollected: 'Geri bildirim toplandı mı?',
    lessonsLearnedDocumented: 'Öğrenilen dersler belgelendi mi?'
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      className={styles.detailsModal}
      size="xl"
      backdropClassName={styles.modalBackdrop}
      contentClassName={styles.modalContent}
    >
      <Modal.Header closeButton className={styles.modalHeader}>
        <div className={styles.modalTitleContainer}>
          <h4 className={styles.modalTitle}>Visit Detayları</h4>
          <Button
            variant="success"
            size="sm"
            onClick={downloadExcel}
            className={styles.downloadButton}
          >
            <FaDownload className={styles.buttonIcon} /> Tabloyu İndir
          </Button>
        </div>
      </Modal.Header>
      <Modal.Body className={styles.modalBody}>
        <div className={styles.visitDetails}>

          {/* Visit-type-specific fields */}
          {isHastane && renderHastaneFields()}
          {isHome && renderHomeFields()}
          {!isHastane && !isHome && (
            <div className={styles.detailSection}>
              <h6 className={styles.sectionTitle}>Temel Bilgiler</h6>
              <div className={styles.infoGrid}>
                <InfoItem label="Tarih" value={val(visit.date)} />
                <InfoItem label="Visit Tipi" value={val(visit.visitType)} />
                <InfoItem label="Başlangıç Saati" value={val(visit.startTime)} />
                <InfoItem label="Bitiş Saati" value={val(visit.endTime)} />
                <InfoItem label="Hasta ID" value={val(visit.patientId)} />
                <InfoItem label="Doktor" value={val(visit.doctor)} />
                <InfoItem label="Hastane" value={val(visit.hospital)} />
                <InfoItem label="Şehir" value={val(visit.city)} />
                <InfoItem label="Seyahat Tipi" value={val(visit.travelType)} />
                <InfoItem label="Durum" value={visit.status ? "Tamamlandı" : "Devam Ediyor"} />
                <InfoItem label="Kaynak Döküman" value={val(visit.sourceDocument)} />
                <InfoItem label="Kaynak Döküman Tamamlanma Saati" value={val(visit.sourceDocumentCompletionTime)} />
                <InfoItem label="Bilgi" value={val(visit.information)} />
              </div>
            </div>
          )}

          {/* Todo List */}
          {Object.keys(visit.todoList || {}).length > 0 && (
            <div className={styles.detailSection}>
              <h6 className={styles.sectionTitle}>Yapılacaklar Listesi</h6>
              <div className={styles.todoGrid}>
                {Object.entries(visit.todoList).map(([key, value]) => {
                  const isPreVisitItem = key.startsWith('pre_');
                  const label = todoLabels[key] || key;
                  return (
                    <div key={key} className={styles.todoItem}>
                      <span className={styles.todoLabel}>
                        {isPreVisitItem ? '🔍 ' : ''}{label}
                      </span>
                      <span className={`${styles.todoStatus} ${value ? styles.completed : styles.pending}`}>
                        {value ? '✓' : '✗'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Other Information */}
          <div className={styles.detailSection}>
            <h6 className={styles.sectionTitle}>Diğer Bilgiler</h6>
            <div className={styles.infoGrid}>
              <InfoItem label="Oluşturulma Tarihi" value={formatTimestamp(visit.createdAt)} />
              <InfoItem label="Son Güncelleme" value={val(visit.lastUpdated)} />
              <InfoItem label="Tamamlanma Tarihi" value={val(visit.completedAt)} />
              <InfoItem label="İlk Visit Tarihi" value={val(visit.firstVisitDate)} />
              <InfoItem label="Çalışan Adı" value={val(visit.employeeName)} />
              <InfoItem label="Seçilen CRA" value={val(craName)} />
              <InfoItem label="Periyot" value={val(visit.period)} />
              <InfoItem label="Opsiyon" value={val(visit.option)} />
            </div>
          </div>

        </div>
      </Modal.Body>
    </Modal>
  );
};

const ProjectVisitsModal = ({ show, onHide, project, employeeId, employeeName }) => {
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [hospitals, setHospitals] = useState({});
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filteredVisits, setFilteredVisits] = useState([]);

  useEffect(() => {
    const fetchHospitalsAndVisits = async () => {
      try {
        // Önce hastaneleri çek
        const centersCollection = collection(db, 'Centers');
        const centersSnapshot = await getDocs(centersCollection);
        const hospitalsMap = {};
        centersSnapshot.docs.forEach(doc => {
          hospitalsMap[doc.id] = doc.data().Adı;
        });
        setHospitals(hospitalsMap);
        
        // Visit koleksiyonunu projeye ve çalışana göre filtrele
        const visitsRef = collection(db, 'Visits');
        const projectVisitsQuery = query(
          visitsRef,
          where('projectId', '==', project.id),
          where('employeeId', '==', employeeId)
        );
        const visitsSnapshot = await getDocs(projectVisitsQuery);

        const visitsData = visitsSnapshot.docs
          .map(doc => {
            const visitData = doc.data();
            return {
              id: doc.id,
              projectId: visitData.projectId,
              ...visitData,
              hospitalName: hospitalsMap[visitData.hospital] || visitData.hospital || '',
              patientId: visitData.patientId || '',
              city: visitData.city || '',
              status: visitData.status || false,
              visitType: visitData.visitType || '',
              date: visitData.date || ''
            };
          })
          .filter(visit => visit.date && visit.date.trim() !== '');

        setVisits(visitsData);

      } catch (error) {
        console.error('Veri çekme hatası:', error);
      } finally {
        setLoading(false);
      }
    };

    if (show && project?.id && employeeId) {
      fetchHospitalsAndVisits();
    }
  }, [show, project?.id, employeeId]);

  useEffect(() => {
    if (visits.length > 0) {
      const filtered = visits.filter(visit => {
        if (!startDate && !endDate) return true;

        const visitDate = new Date(visit.date);
        const start = startDate ? new Date(startDate) : null;
        const end = endDate ? new Date(endDate) : null;

        if (start && end) {
          return visitDate >= start && visitDate <= end;
        } else if (start) {
          return visitDate >= start;
        } else if (end) {
          return visitDate <= end;
        }
        return true;
      });
      setFilteredVisits(filtered);
    }
  }, [visits, startDate, endDate]);

  const getVisitTypeLabel = (type) => {
    const types = {
      hastane: 'Hastane',
      home: 'Home',
      telefon: 'Telefon',
      unplanning: 'Unplanning',
      handover: 'Handover'
    };
    return types[type] || type;
  };

  const handleShowDetails = (visit) => {
    setSelectedVisit(visit);
  };

  const handleDownloadVisits = () => {
    const rows = filteredVisits.map(visit => ({
      'Tarih': visit.date || '-',
      'Visit Tipi': getVisitTypeLabel(visit.visitType) || '-',
      'Hasta ID': visit.patientId || '-',
      'Hastane': visit.hospitalName || '-',
      'Şehir': visit.city || '-',
      'Durum': visit.status ? 'Tamamlandı' : 'Devam Ediyor'
    }));

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Proje Visitleri');

    const projectLabel = project?.titleShort || project?.title || 'Proje';
    const fileName = `Proje_Visitleri_${projectLabel}${employeeName ? `_${employeeName}` : ''}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  return (
    <>
      <Modal 
        show={show} 
        onHide={onHide} 
        size="xl"
        className={styles.visitsModal}
        backdropClassName={styles.modalBackdrop}
        contentClassName={styles.modalContent}
      >
        <Modal.Header closeButton className={styles.modalHeader}>
          <div className={styles.modalTitleContainer}>
            <h4 className={styles.modalTitle}>
              Proje Visitleri - {project?.titleShort || project?.title || 'N/A'}
              {employeeName && ` | ${employeeName}`}
            </h4>
            <div className={styles.headerActions}>
              <Badge
                bg="info"
                className={styles.visitCountBadge}
              >
                Toplam Visit: {filteredVisits.length}
              </Badge>
              <Button
                variant="success"
                size="sm"
                onClick={handleDownloadVisits}
                disabled={filteredVisits.length === 0}
                className={styles.downloadButton}
              >
                <FaDownload className={styles.buttonIcon} /> Excel İndir
              </Button>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body className={styles.modalBody}>
          {loading ? (
            <div className={styles.loadingContainer}>
              <Spinner animation="border" className={styles.spinner} />
            </div>
          ) : visits.length > 0 ? (
            <>
              <div className={styles.filterContainer}>
                <div className={styles.dateFilterItem}>
                  <label className={styles.filterLabel}>
                    <FaCalendarAlt className={styles.filterIcon} /> Başlangıç Tarihi
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className={styles.dateInput}
                  />
                </div>
                <div className={styles.dateFilterItem}>
                  <label className={styles.filterLabel}>
                    <FaCalendarAlt className={styles.filterIcon} /> Bitiş Tarihi
                  </label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className={styles.dateInput}
                  />
                </div>
              </div>
              
              <div className={styles.tableContainer}>
                <table className={styles.visitsTable}>
                  <thead>
                    <tr>
                      <th><FaCalendarAlt className={styles.columnIcon} /> Tarih</th>
                      <th>Visit Tipi</th>
                      <th>Hasta ID</th>
                      <th><FaHospital className={styles.columnIcon} /> Hastane</th>
                      <th><FaMapMarkerAlt className={styles.columnIcon} /> Şehir</th>
                      <th>Durum</th>
                      <th>İşlem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredVisits.map(visit => (
                      <tr key={visit.id} className={styles.tableRow}>
                        <td>{visit.date || "-"}</td>
                        <td>{getVisitTypeLabel(visit.visitType) || "-"}</td>
                        <td>{visit.patientId || "-"}</td>
                        <td>{visit.hospitalName || "-"}</td>
                        <td>{visit.city || "-"}</td>
                        <td>
                          <span className={`${styles.statusBadge} ${visit.status ? styles.completed : styles.pending}`}>
                            {visit.status ? "Tamamlandı" : "Devam Ediyor"}
                          </span>
                        </td>
                        <td>
                          <button
                            onClick={() => handleShowDetails(visit)}
                            disabled={!visit.status}
                            className={`${styles.detailButton} ${!visit.status ? styles.disabled : ''}`}
                            title={!visit.status ? 'Visit devam ettiği için detaylar görüntülenemez' : ''}
                          >
                            <FaEye className={styles.buttonIcon} /> Detay
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className={styles.emptyState}>
              <p>Bu proje için hiç Visit bulunamadı</p>
            </div>
          )}
        </Modal.Body>
      </Modal>

      {selectedVisit && (
        <VisitDetailsModal
          show={!!selectedVisit}
          onHide={() => setSelectedVisit(null)}
          visit={{
            ...selectedVisit,
            hospital: hospitals[selectedVisit.hospital] || selectedVisit.hospital
          }}
        />
      )}
    </>
  );
};

export default ProjectVisitsModal;